/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import {Routes, Route, Navigate, useLocation, useNavigate} from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeColors from "assets/theme/base/colors";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { Toaster } from 'react-hot-toast';
// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import MDLoader from "./components/MDLoader";
import jwt_decode from "jwt-decode";
import LanguageSwitcher from "./components/Utils/LanguageSwitcher";
import {useTranslation} from "react-i18next";


export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    user,
    token,
    refreshToken
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const {i18n } = useTranslation();
  // const {permissions} = jwt_decode(token);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if ((pathname === "/login" || pathname === "/reset"  || pathname === "/forgot-password") && refreshToken !== "") {
      navigate('/dashboard');
    }
  }, [pathname]);

  const getRoutes = useMemo(() => {
    const generateRoutes = (routes) =>
      routes.map((route) => {
        if (route.collapse) {
          return generateRoutes(route.collapse);
        }
        if(route.key === "users" && user.role === "MAKER") return;

        // Consolidated pages: visible when the user holds ANY of the listed permissions
        if (token && route.anyOfPermissions) {
          const { permissions } = jwt_decode(token);
          const hasAny = route.anyOfPermissions.some((permissionKey) =>
            permissions.includes(permissionKey)
          );
          if (route.route && hasAny) {
            return <Route exact path={route.route} element={route.component} key={route.key} />;
          }
          return null;
        }

        // Check if module and subModule properties exist
        if (token && route.module && route.subModule) {
          const {permissions} = jwt_decode(token);
          // console.log("permissions",permissions);
          const permissionKey = `${route.module}:${route.subModule}`;

          // Only render the route if the corresponding permission exists
          if (token && route.route && permissions.includes(permissionKey)) {
            return <Route exact path={route.route} element={route.component} key={route.key} />;
          }
        } else if (route.route) {
          // If module and subModule do not exist, render the route
          return <Route exact path={route.route} element={route.component} key={route.key} />;
        }

        return null;
      });

    return generateRoutes(routes);
  }, [token, routes, i18n.language]);

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="YC Admin Portal"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            {/* Rendered once here (like the sidenav) so it never unmounts /
                flashes on navigation — DashboardLayout only fades the page
                content, not this. */}
            <DashboardNavbar />
            <Configurator />
            {/*{configsButton}*/}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <MDLoader />
      <LanguageSwitcher />
      <Toaster
        containerClassName="toaster-my"
        position="top-right"
        gutter={10}
        toastOptions={{
          duration: 4000,
          style: {
            background: "#ffffff",
            color: themeColors.dark.main,
            borderRadius: "8px",
            border: `1px solid ${themeColors.grey[300]}`,
            borderLeft: `4px solid ${themeColors.secondary.main}`,
            boxShadow: "0 1px 2px rgba(20,22,35,0.06), 0 4px 16px rgba(20,22,35,0.06)",
            padding: "12px 16px",
            fontSize: "13px",
            fontWeight: "500",
            maxWidth: "400px",
            lineHeight: "1.5",
          },
          success: {
            duration: 3500,
            style: { borderLeft: `4px solid ${themeColors.success.main}` },
            iconTheme: { primary: themeColors.success.main, secondary: "#ffffff" },
          },
          error: {
            duration: 5000,
            style: { borderLeft: `4px solid ${themeColors.error.main}` },
            iconTheme: { primary: themeColors.error.main, secondary: "#ffffff" },
          },
          loading: {
            style: { borderLeft: `4px solid ${themeColors.info.main}` },
            iconTheme: { primary: themeColors.info.main, secondary: "#ffffff" },
          },
        }}
      />
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="YC Admin Portal"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          {/* Rendered once here (like the sidenav) so it never unmounts /
              flashes on navigation — DashboardLayout only fades the page
              content, not this. */}
          <DashboardNavbar />
          <Configurator />
          {/*{configsButton}*/}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes}
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </ThemeProvider>
  );
}
